import type { LocalActivity, LocalMessage, ThreadSummary } from '@swchat/client-sdk';
import { useEffect, useState } from 'react';
import { Avatar, Icon } from '../design';
import { activityText } from '../lib/activity';
import { quoteText } from '../lib/display';
import { useClient } from '../lib/hooks';
import { shortTime } from './format';

/**
 * W2.2: tab "Luồng trao đổi" — mỗi luồng: tin gốc, số phản hồi, giờ phản hồi cuối; bấm để mở khung luồng.
 */
export function ThreadList({
  conversationId,
  threads,
  senderName,
  onOpen,
}: {
  conversationId: string;
  threads: ThreadSummary[];
  senderName: (userId: string) => string | undefined;
  onOpen: (rootId: string) => void;
}) {
  const client = useClient();
  const [roots, setRoots] = useState<Map<string, LocalMessage | null>>(new Map());
  useEffect(() => {
    let alive = true;
    void Promise.all(threads.map(async (t) => [t.root_id, (await client.getMessage(conversationId, t.root_id)) ?? null] as const)).then(
      (found) => alive && setRoots(new Map(found)),
    );
    return () => {
      alive = false;
    };
  }, [client, conversationId, threads]);

  if (!threads.length) {
    return (
      <div className="ws-tab-empty" data-testid="thread-list">
        <Icon name="chat" size={22} />
        <p>Chưa có luồng trao đổi nào. Chọn “Trả lời trong luồng” trên một tin để bắt đầu.</p>
      </div>
    );
  }
  return (
    <ul className="ws-tab-list" data-testid="thread-list" aria-label="Luồng trao đổi">
      {threads.map((t) => {
        const root = roots.get(t.root_id);
        const q = quoteText(root ?? null, senderName);
        return (
          <li key={t.root_id}>
            <button type="button" className="ws-thread-item" data-testid="thread-item" data-root-id={t.root_id} onClick={() => onOpen(t.root_id)}>
              <Avatar name={q.sender ?? '…'} seed={root?.sender_user_id ?? t.root_id} size="sm" />
              <span className="ws-thread-item-body">
                <span className="ws-thread-item-root">
                  {q.sender && <strong>{q.sender}: </strong>}
                  {q.text}
                </span>
                <span className="ws-small ws-muted">
                  {t.reply_count} phản hồi · trả lời cuối {shortTime(t.last_ts)}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * W2.4: tab "Hoạt động" — sự kiện hệ thống (thêm/xóa/rời, đổi tên, đổi vai trò, ghim) suy ra trên thiết bị này, mới
 * nhất ở đầu. Không có dữ liệu từ server ngoài danh sách thành viên/vai trò đã có.
 */
export function ActivityList({
  activity,
  me,
  senderName,
}: {
  activity: LocalActivity[];
  me?: string;
  senderName: (userId: string) => string | undefined;
}) {
  const name = (id: string) => (id === me ? 'Bạn' : (senderName(id) ?? '…'));
  if (!activity.length) {
    return (
      <div className="ws-tab-empty" data-testid="activity-list">
        <Icon name="clock" size={22} />
        <p>Chưa có hoạt động nào trên thiết bị này.</p>
      </div>
    );
  }
  return (
    <ol className="ws-tab-list ws-activity" data-testid="activity-list" aria-label="Hoạt động">
      {[...activity].reverse().map((a) => (
        <li key={a.id} className="ws-activity-item" data-testid="activity-item" data-kind={a.kind}>
          <Icon name={ACTIVITY_ICON[a.kind] ?? 'clock'} size={15} />
          <span className="ws-activity-text">{activityText(a, name)}</span>
          <time className="ws-small ws-muted" dateTime={new Date(a.ts).toISOString()}>
            {shortTime(a.ts)}
          </time>
        </li>
      ))}
    </ol>
  );
}

const ACTIVITY_ICON: Record<LocalActivity['kind'], string> = {
  member_added: 'people',
  member_removed: 'people',
  member_left: 'back',
  renamed: 'edit',
  role_changed: 'shield',
  pinned: 'pin',
  unpinned: 'pin',
};
